import { h, FunctionalComponent } from "preact";
import { useState } from "preact/hooks";
import { CollectionProvider, useCollection } from "./CollectionContext";
import FiltersForm from "./FiltersForm";
import ProductsGrid from "./ProductGrid";
import SortSelect from "./SortSelect";
import GridSwitch from "./GridSwitch";

interface ProductCollectionProps {
  collectionHandle: string;
  collectionId: number;
  productsPerPage: number;
  searchTerm: string | "";
  pages: number;
}

const Collection: FunctionalComponent<ProductCollectionProps> = ({
  searchTerm,
}) => {
  const [columns, setColumns] = useState(4);

  const handleFilterDrawerToggle = () => {
    const drawer = document.querySelector(".collection__filters-wrapper");
    drawer?.classList.toggle("collection__filters-wrapper--open");

    const overlay = document.querySelector(".collection__filters-overlay");

    overlay?.classList.toggle("collection__filters-overlay--open");

    overlay?.addEventListener("click", () => {
      drawer?.classList.remove("collection__filters-wrapper--open");
      overlay?.classList.remove("collection__filters-overlay--open");
    });
  };

  const handleGridSwitch = (cols: number) => {
    setColumns(cols);
  };

  const { clearFilters, selectedFilters } = useCollection();

  return (
    <div className="collection">
      <div className="collection__actions">
        <div className="container collection__actions-container">
          <div className="collection__filters-wrapper">
            <div className="collection__filters-header hide-on-desktop">
              <h3
                className="collection__filters-title heading-3 hide-on-desktop"
                dangerouslySetInnerHTML={{
                  __html: window.themeVars.strings.collection.filtersButton,
                }}
              ></h3>
              <button
                className="collection__filters-close hide-on-desktop"
                onClick={handleFilterDrawerToggle}
                dangerouslySetInnerHTML={{
                  __html: window.themeVars.icons.close,
                }}
              ></button>
            </div>
            <CollectionInfo query={searchTerm} />
            <FiltersForm query={searchTerm} />
            <SortSelect />
            <button
              className="collection__filters-apply btn btn-black hide-on-desktop"
              onClick={handleFilterDrawerToggle}
              dangerouslySetInnerHTML={{
                __html: window.themeVars.strings.collection.applyFilters,
              }}
            ></button>
            {selectedFilters.length > 1 && (
              <button
                className="collection__filters-clear btn btn-transparent"
                onClick={clearFilters}
                dangerouslySetInnerHTML={{
                  __html: window.themeVars.strings.collection.clearFilters,
                }}
              ></button>
            )}
            <GridSwitch
              onSwitch={handleGridSwitch}
              switcherClass={"hide-on-mobile"}
            />
          </div>

          <div className="collection__filters-overlay"></div>

          <button
            className="collection__filter-button btn btn-transparent hide-on-desktop"
            onClick={handleFilterDrawerToggle}
            dangerouslySetInnerHTML={{
              __html: window.themeVars.strings.collection.filtersButton,
            }}
          ></button>
          <GridSwitch
            onSwitch={handleGridSwitch}
            switcherClass={"hide-on-desktop"}
          />
        </div>

        <SelectedFilters />
      </div>
      <ProductsGrid columns={columns} query={searchTerm} />
    </div>
  );
};

interface CollectionInfoProps {
  query: string;
}

const CollectionInfo: FunctionalComponent<CollectionInfoProps> = ({
  query,
}) => {
  const { productCount, products } = useCollection();
  const template = window.themeVars.store.template;

  let showLoadingState = false;

  if (products === "pending") {
    showLoadingState = true;

    if (template === "search" && !query) {
      showLoadingState = false;
    }
  }

  if (showLoadingState) {
    return (
      <div
        className="
        collection__product-count
        is-loading__skeleton
      "
      ></div>
    );
  }

  return (
    <h4 className="collection__product-count heading-4">
      {productCount} {window.themeVars.strings.collection.products}
    </h4>
  );
};

const SelectedFilters: FunctionalComponent = () => {
  const { selectedFilters, setSelectedFilters } = useCollection();

  const handleFilterValueRemove = (param_name: string, value: string) => {
    const newFilters = selectedFilters
      .map((filter) => {
        if (filter.param_name === param_name) {
          const newValues = filter.values.filter((v) => v !== value);
          return { ...filter, values: newValues };
        }
        return filter;
      })
      .filter((filter) => filter.values.length > 0); // Remove filter entirely if no values left

    setSelectedFilters(newFilters);
  };

  if (!selectedFilters.length) {
    return null;
  }

  return (
    <div className="collection__selected-filters">
      <div className="collection__selected-filters container">
        <ul className="collection__selected-filter-values">
          {selectedFilters.flatMap((filter) =>
            filter.values.map((value) => (
              <li key={`${filter.param_name}-${value}`}>
                <span>{value}</span>
                <button
                  className={"collection__selected-filter-remove"}
                  dangerouslySetInnerHTML={{
                    __html: window.themeVars.icons.close,
                  }}
                  onClick={() =>
                    handleFilterValueRemove(filter.param_name, value)
                  }
                ></button>
              </li>
            )),
          )}
        </ul>
      </div>
    </div>
  );
};

const CollectionWrapper: FunctionalComponent<ProductCollectionProps> = (
  props,
) => {
  return (
    <CollectionProvider
      currentPage={1}
      pages={props.pages}
      productsPerPage={props.productsPerPage}
      searchTerm={props.searchTerm}
      collectionId={props.collectionId}
    >
      <Collection {...props} />
    </CollectionProvider>
  );
};

export default CollectionWrapper;
